import React, { useState } from 'react';
import { saveAs } from 'file-saver';
import Logout from '../components/Logout';

const Home = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [documents, setDocuments] = useState([]);
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [processingStep, setProcessingStep] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');

  // ===== ПОДДЕРЖКА И РОП, И АРОП =====
  const getDocumentOrder = () => {
    if (documents.length === 0) return ['РОП_БЗ', 'РОП_БЗП', 'РОП_КП', 'РОП_КПД', 'РОП_КПП'];
    
    const hasArop = documents.some(d => d.type && d.type.startsWith('АРОП_'));
    const hasRop = documents.some(d => d.type && d.type.startsWith('РОП_'));
    
    if (hasArop) {
      return ['АРОП_БЗ', 'АРОП_БЗП', 'АРОП_КП', 'АРОП_КПД', 'АРОП_КПП'];
    }
    return ['РОП_БЗ', 'РОП_БЗП', 'РОП_КП', 'РОП_КПД', 'РОП_КПП'];
  };

  // ===== НОВЫЕ МАППИНГИ ДЛЯ ОТОБРАЖЕНИЯ =====
  const DOCUMENT_SHORT_NAMES = {
    'РОП_БЗ': 'БЗ',
    'РОП_БЗП': 'БЗП',
    'РОП_КП': 'КП',
    'РОП_КПД': 'КПД',
    'РОП_КПП': 'КПП',
    'АРОП_БЗ': 'БЗ',
    'АРОП_БЗП': 'БЗП',
    'АРОП_КП': 'КП',
    'АРОП_КПД': 'КПД',
    'АРОП_КПП': 'КПП'
  };

  const DOCUMENT_FULL_NAMES = {
    'РОП_БЗ': 'Бланк заказа (РОП)',
    'РОП_БЗП': 'Бланк заказа партнера (РОП)',
    'РОП_КП': 'Коммерческое предложение (РОП)',
    'РОП_КПД': 'Коммерческое предложение с детализацией (РОП)',
    'РОП_КПП': 'Коммерческое предложение для партнера (РОП)',
    'АРОП_БЗ': 'Бланк заказа (АРОП)',
    'АРОП_БЗП': 'Бланк заказа партнера (АРОП)',
    'АРОП_КП': 'Коммерческое предложение (АРОП)',
    'АРОП_КПД': 'Коммерческое предложение с детализацией (АРОП)',
    'АРОП_КПП': 'Коммерческое предложение для партнера (АРОП)'
  };

  // Функция для получения отображаемого названия в зависимости от статуса
  const getDisplayName = (docType, status) => {
    const shortName = DOCUMENT_SHORT_NAMES[docType] || docType;
    const fullName = DOCUMENT_FULL_NAMES[docType] || docType;
    
    // Если документ готов или скачивается - показываем полное название
    if (status === 'ready' || status === 'success' || status === 'downloading') {
      return `${docType} (${fullName})`;
    }
    // Иначе показываем короткое название
    return shortName;
  };

  const DOCUMENT_ORDER = getDocumentOrder();

  const isValidFile = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['xlsx', 'xls', 'csv'].includes(ext);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!isValidFile(file.name)) {
        setMessage('❌ Пожалуйста, выберите Excel-файл (.xlsx, .xls, .csv)');
        setMessageType('error');
        setSelectedFile(null);
        setDocuments([]);
        setDocumentStatuses({});
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      setMessage('');
      setMessageType('');
      setDocuments([]);
      setDocumentStatuses({});
      setProcessingStep('');
      setProgress(0);
      setProgressText('');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getStatusEmoji = (status) => {
    switch (status) {
      case 'waiting': return '⏳';
      case 'processing': return '⏳';
      case 'ready': return '✅';
      case 'downloading': return '📥';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '⏳';
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'waiting': return { color: '#9CA3AF' };
      case 'processing': return { color: '#8B5CF6' };
      case 'ready': return { color: '#10B981' };
      case 'downloading': return { color: '#8B5CF6' };
      case 'success': return { color: '#10B981' };
      case 'error': return { color: '#EF4444' };
      default: return { color: '#9CA3AF' };
    }
  };

  const updateDocumentStatus = (docType, status, message, hasData = false) => {
    setDocumentStatuses(prev => ({
      ...prev,
      [docType]: {
        status: status,
        message: message,
        hasData: hasData,
        error: null
      }
    }));
  };

  const simulateProcessing = (documentsData) => {
    return new Promise(async (resolve) => {
      const docTypes = documentsData.map(d => d.type || 'Документ');
      const totalDocs = docTypes.length;
      const progressPerDoc = 35 / totalDocs;
      let currentProgress = 60;

      for (let i = 0; i < totalDocs; i++) {
        const docType = docTypes[i];
        updateDocumentStatus(docType, 'processing', 'Генерация...');
        
        const progressStart = currentProgress;
        const progressEnd = currentProgress + progressPerDoc;
        
        const steps = 5;
        const stepSize = (progressEnd - progressStart) / steps;
        
        for (let step = 0; step < steps; step++) {
          const newProgress = progressStart + (stepSize * (step + 1));
          setProgress(Math.min(newProgress, progressEnd));
          
          const substeps = [
            '📝 Подготовка данных...',
            '🔄 Генерация документа...',
            '📊 Форматирование таблиц...',
            '🎨 Применение стилей...',
            '📄 Конвертация в PDF...'
          ];
          // Используем короткое название в прогрессе
          const shortName = DOCUMENT_SHORT_NAMES[docType] || docType;
          setProgressText(`${substeps[step]} (${shortName})`);
          
          await new Promise(r => setTimeout(r, 300 + Math.random() * 400));
        }
        
        const doc = documentsData.find(d => (d.type || 'Документ') === docType);
        const hasData = !!(doc?.pdf || doc?.pdfUrl);
        updateDocumentStatus(
          docType, 
          hasData ? 'ready' : 'error', 
          hasData ? '✅ Готов к скачиванию' : '❌ Ошибка генерации',
          hasData
        );
        
        currentProgress = progressEnd;
        setProgress(currentProgress);
      }

      setProgress(95);
      setProgressText('📦 Финализация...');
      await new Promise(r => setTimeout(r, 500));
      
      setProgress(100);
      setProgressText('✅ Готово!');
      resolve();
    });
  };

  const handleDownload = async (doc, docType) => {
    if (!doc) return false;
    
    console.log('📥 Скачиваем PDF:', docType);
    console.log('🔗 S3 URL:', doc.pdfUrl);
    
    setDocumentStatuses(prev => ({
      ...prev,
      [docType]: {
        ...prev[docType],
        status: 'downloading',
        message: '📥 Скачивание...'
      }
    }));

    try {
      if (doc.pdfUrl) {
        const link = document.createElement('a');
        link.href = doc.pdfUrl;
        link.download = doc.filename || `${docType}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setDocumentStatuses(prev => ({
          ...prev,
          [docType]: {
            ...prev[docType],
            status: 'success',
            message: '✅ Скачан!'
          }
        }));
        return true;
      } else if (doc.pdf) {
        const byteCharacters = atob(doc.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = doc.filename || `${docType}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(link.href), 5000);
        
        setDocumentStatuses(prev => ({
          ...prev,
          [docType]: {
            ...prev[docType],
            status: 'success',
            message: '✅ Скачан!'
          }
        }));
        return true;
      } else {
        throw new Error('Нет данных для скачивания');
      }
    } catch (error) {
      console.error('❌ Ошибка:', error);
      setDocumentStatuses(prev => ({
        ...prev,
        [docType]: {
          ...prev[docType],
          status: 'error',
          message: `❌ Ошибка: ${error.message}`
        }
      }));
      return false;
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage('❌ Выберите файл');
      setMessageType('error');
      return;
    }

    setUploading(true);
    setIsProcessing(true);
    setProgress(0);
    setProgressText('🔄 Подготовка к загрузке...');
    setMessage('⏳ Обработка файла...');
    setMessageType('info');
    setDocuments([]);
    setDocumentStatuses({});
    setProcessingStep('🔄 Загрузка файла...');

    const jwtToken = localStorage.getItem('access_token');
    if (!jwtToken) {
      setMessage('❌ Нет токена авторизации. Выйдите и войдите снова.');
      setMessageType('error');
      setUploading(false);
      setIsProcessing(false);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(selectedFile);
    
    reader.onload = async () => {
      const base64File = reader.result.split(',')[1];

      try {
        setProgress(5);
        setProgressText('📂 Чтение файла...');
        await new Promise(r => setTimeout(r, 300));
        setProgress(10);
        setProgressText('📤 Подготовка данных...');

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000);

        setProcessingStep('🔄 Отправка данных на сервер...');
        setProgress(15);
        setProgressText('🚀 Отправка на сервер...');
        await new Promise(r => setTimeout(r, 200));
        setProgress(20);
        setProgressText('⏳ Ожидание ответа сервера...');

        const response = await fetch('/api/process', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({
            file: base64File,
            filename: selectedFile.name,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        setProgress(35);
        setProgressText('📥 Обработка ответа...');

        if (!response.ok) {
          let errorMessage = 'Ошибка сервера';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        setProgress(40);
        setProgressText('📊 Парсинг данных...');
        
        const data = await response.json();
        console.log('✅ Response data:', data);
        
        setProgress(50);
        setProgressText('📑 Подготовка к генерации документов...');
        await new Promise(r => setTimeout(r, 300));

        if (data.documents && data.documents.length > 0) {
          setDocuments(data.documents);
          
          const initialStatuses = {};
          data.documents.forEach((doc) => {
            const docType = doc.type || `Документ`;
            initialStatuses[docType] = {
              status: 'waiting',
              message: 'Ожидание...',
              hasData: false,
              error: null
            };
          });
          setDocumentStatuses(initialStatuses);
          
          setProgress(55);
          setProgressText('🔄 Начало генерации...');
          await new Promise(r => setTimeout(r, 200));
          
          await simulateProcessing(data.documents);

          const successCount = data.documents.filter(d => d.pdf || d.pdfUrl).length;
          const errorCount = data.documents.length - successCount;
          
          let msg = `✅ Сгенерировано ${successCount} документов`;
          if (errorCount > 0) {
            msg += `, ошибок: ${errorCount}`;
          }
          setMessage(msg);
          setMessageType(successCount > 0 ? 'success' : 'error');
          setProcessingStep('✅ Генерация завершена!');
          
          setSelectedFile(null);
          const fileInput = document.querySelector('input[type="file"]');
          if (fileInput) fileInput.value = '';
        } else {
          setMessage('❌ Не найдено документов для генерации');
          setMessageType('error');
          setProgress(0);
        }
      } catch (error) {
        console.error('❌ Upload error:', error);
        setProgress(0);
        setProgressText('');
        
        if (error.name === 'AbortError') {
          setMessage('❌ Превышено время ожидания. Попробуйте файл меньшего размера.');
        } else if (error.message === 'Failed to fetch') {
          setMessage('❌ Ошибка сети. Проверьте подключение к интернету.');
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          setMessage('❌ Ошибка авторизации. Выйдите и войдите снова.');
          localStorage.removeItem('access_token');
        } else {
          setMessage(`❌ Ошибка: ${error.message}`);
        }
        setMessageType('error');
      } finally {
        setUploading(false);
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setMessage('❌ Ошибка чтения файла');
      setMessageType('error');
      setUploading(false);
      setIsProcessing(false);
      setProgress(0);
      setProgressText('');
    };
  };

  const getMessageStyle = () => {
    switch (messageType) {
      case 'success':
        return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #A5D6A7' };
      case 'error':
        return { backgroundColor: '#FFEBEE', color: '#C62828', border: '1px solid #FFCDD2' };
      case 'info':
        return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #BBDEFB' };
      default:
        return { backgroundColor: '#F5F5F5', color: '#1E2A3A', border: '1px solid #E0E0E0' };
    }
  };

  const handleDownloadClick = (doc, docType, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!doc) return;
    handleDownload(doc, docType);
  };

  const currentOrder = getDocumentOrder();

  return (
    <div style={styles.container}>
      <Logout />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo}>
            <span style={styles.logoIcon}>📄</span>
            <span style={styles.logoText}>Сервис генерации документов</span>
          </div>
          <p style={styles.logoSubtext}>на базе конфигурационного файла СРТП (Excel to PDF)</p>
          <p style={styles.headerSubtitle}>В рамках проекта по автоматизации СРТП</p>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.card}>
          <div style={styles.instructions}>
            <h3 style={styles.instructionsTitle}>Требования к файлу</h3>
            <ul style={styles.instructionsList}>
              <li>Формат: Excel (.xlsx, .xls, .csv)</li>
              <li>Максимальный размер: 10 МБ</li>
              <li style={{ marginTop: '8px' }}>
                ⚠️ <strong>Важно:</strong> Генерироваться будут только те документы, 
                одноимённые листы которых в Excel имеют статус <strong>"OK"</strong> в ячейке C2
              </li>
              <li style={{ marginTop: '12px' }}>
                <strong>Для генерации документов РОП</strong> файл должен содержать листы с вкладками:
              </li>
              <ul style={styles.subList}>
                <li>📄 <strong>РОП_БЗ</strong> — Бланк заказа (РОП)</li>
                <li>📄 <strong>РОП_БЗП</strong> — Бланк заказа партнера (РОП)</li>
                <li>📄 <strong>РОП_КП</strong> — Коммерческое предложение (РОП)</li>
                <li>📄 <strong>РОП_КПД</strong> — Коммерческое предложение с детализацией (РОП)</li>
                <li>📄 <strong>РОП_КПП</strong> — Коммерческое предложение для партнера (РОП)</li>
              </ul>
              <li style={{ marginTop: '12px' }}>
                <strong>Для генерации документов АРОП</strong> файл должен содержать листы с вкладками:
              </li>
              <ul style={styles.subList}>
                <li>📄 <strong>АРОП_БЗ</strong> — Бланк заказа (АРОП)</li>
                <li>📄 <strong>АРОП_БЗП</strong> — Бланк заказа партнера (АРОП)</li>
                <li>📄 <strong>АРОП_КП</strong> — Коммерческое предложение (АРОП)</li>
                <li>📄 <strong>АРОП_КПД</strong> — Коммерческое предложение с детализацией (АРОП)</li>
                <li>📄 <strong>АРОП_КПП</strong> — Коммерческое предложение для партнера (АРОП)</li>
              </ul>
              <li style={{ marginTop: '12px', color: '#6B7280', fontSize: '13px' }}>
                💡 <strong>Примечание:</strong> В одном файле могут быть листы только одного типа — либо РОП, либо АРОП
              </li>
              <li style={{ marginTop: '4px' }}>
                📥 Нажмите на кнопку, чтобы скачать PDF
              </li>
            </ul>
          </div>

          <div style={styles.uploadArea}>
            <div style={styles.fileZone}>
              <label style={styles.fileLabel}>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  disabled={uploading}
                  style={styles.fileInput}
                />
                <div style={styles.fileButton}>
                  <span style={styles.fileIcon}>📁</span>
                  {selectedFile ? selectedFile.name : 'Выберите Excel-файл'}
                </div>
              </label>

              {selectedFile && !uploading && (
                <div style={styles.fileInfo}>
                  <span style={styles.fileInfoIcon}>📎</span>
                  <span>{selectedFile.name}</span>
                  <span style={styles.fileSize}>({formatFileSize(selectedFile.size)})</span>
                </div>
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
              style={{
                ...styles.uploadButton,
                ...(uploading || !selectedFile ? styles.buttonDisabled : styles.buttonActive)
              }}
            >
              {uploading ? (
                <>
                  <span style={styles.spinner}></span>
                  {processingStep || 'Генерация документов...'}
                </>
              ) : (
                '🚀 Сформировать документы'
              )}
            </button>
          </div>

          {uploading && (
            <div style={styles.progressContainer}>
              <div style={styles.progressWrapper}>
                <div style={styles.progressTrack}>
                  <div 
                    style={{
                      ...styles.progressBar,
                      width: `${Math.min(progress, 100)}%`
                    }}
                  />
                </div>
                <div style={styles.progressLabel}>
                  <span style={styles.progressText}>
                    {progressText || (progress < 100 ? `⏳ Генерация... ${Math.round(progress)}%` : '✅ Готово!')}
                  </span>
                  <span style={styles.progressPercent}>{Math.round(progress)}%</span>
                </div>
              </div>
            </div>
          )}

          {message && (
            <div style={{ ...styles.message, ...getMessageStyle() }}>
              {message}
            </div>
          )}

          {(Object.keys(documentStatuses).length > 0 || isProcessing) && (
            <div style={styles.documentsList}>
              <div style={styles.documentsHeader}>
                <h4 style={styles.documentsTitle}>📑 Статус генерации документов:</h4>
              </div>
              
              {currentOrder.map((docType) => {
                const doc = documents.find(d => (d.type || d) === docType);
                const status = documentStatuses[docType] || { 
                  status: 'waiting', 
                  message: 'Ожидание...',
                  hasData: false
                };
                const hasData = !!(doc?.pdf || doc?.pdfUrl);
                const isWaiting = status.status === 'waiting';
                const isProcessing2 = status.status === 'processing';
                const isReady = status.status === 'ready' && hasData;
                const isError = status.status === 'error' || (!hasData && status.status === 'ready');
                
                if (!doc && !isProcessing) return null;
                
                // Получаем отображаемое название в зависимости от статуса
                const displayName = getDisplayName(docType, status.status);
                
                return (
                  <div key={docType} style={styles.docItem}>
                    <div style={styles.docItemLeft}>
                      <span style={{ ...styles.docStatusIcon, ...getStatusStyle(status.status) }}>
                        {isWaiting ? '⏳' : 
                         isProcessing2 ? '⏳' : 
                         isReady ? '✅' : 
                         isError ? '❌' : getStatusEmoji(status.status)}
                      </span>
                      <span style={styles.docType}>
                        {displayName}
                      </span>
                      <span style={{ ...styles.docStatusText, ...getStatusStyle(status.status) }}>
                        {isProcessing2 ? 'Генерация...' : status.message}
                      </span>
                      {isProcessing2 && <span style={styles.processingDots}></span>}
                    </div>
                    <div style={styles.docItemRight}>
                      {isReady && status.status !== 'downloading' && (
                        <button
                          onClick={(e) => handleDownloadClick(doc, docType, e)}
                          style={styles.docLink}
                          type="button"
                        >
                          📥 Скачать PDF
                        </button>
                      )}
                      {isReady && doc?.size && (
                        <span style={styles.docSize}>
                          {(doc.size / 1024).toFixed(1)} KB
                        </span>
                      )}
                      {status.status === 'downloading' && (
                        <span style={styles.docDownloading}>📥 Скачивание...</span>
                      )}
                      {isError && !hasData && (
                        <span style={styles.docError}>❌ {doc?.error || 'Ошибка генерации'}</span>
                      )}
                      {isWaiting && !isProcessing && (
                        <span style={styles.docWaiting}>⏳ Ожидание...</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.features}>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>⚙️</span>
              <div>
                <h4 style={styles.featureTitle}>Качественная обработка</h4>
                <p style={styles.featureDesc}>Генерация до 2-х минут</p>
              </div>
            </div>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>🔒</span>
              <div>
                <h4 style={styles.featureTitle}>Точное форматирование</h4>
                <p style={styles.featureDesc}>LibreOffice на сервере</p>
              </div>
            </div>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>📄</span>
              <div>
                <h4 style={styles.featureTitle}>5 документов</h4>
                <p style={styles.featureDesc}>БЗ, БЗП, КП, КПД, КПП</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.footer}>
        <div style={styles.footerContent}>
          <p>© 2026 Сервис генерации документов</p>
          <p style={styles.footerLink}>Техническая поддержка: <a href="mailto:@a.krasikova" style={styles.footerEmail}>@a.krasikova</a></p>
        </div>
      </div>
    </div>
  );
};

// ==================== СТИЛИ ====================
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F8F9FA',
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif'
  },
  header: {
    backgroundColor: '#0B1C3F',
    padding: '60px 20px',
    textAlign: 'center'
  },
  headerContent: {
    maxWidth: '900px',
    margin: '0 auto'
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '16px'
  },
  logoIcon: {
    fontSize: '48px'
  },
  logoText: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: '-0.5px'
  },
  logoSubtext: {
    fontSize: '16px',
    color: '#B0BEC5',
    margin: '0 0 16px 0',
    fontWeight: '400'
  },
  headerSubtitle: {
    fontSize: '16px',
    color: '#B0BEC5',
    margin: 0,
    lineHeight: 1.5,
    fontWeight: '400'
  },
  main: {
    maxWidth: '1000px',
    margin: '-40px auto 60px',
    padding: '0 20px'
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    padding: '40px',
    border: '1px solid #E8ECF0'
  },
  instructions: {
    backgroundColor: '#F8F9FC',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '32px',
    border: '1px solid #E8ECF0'
  },
  instructionsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#0B1C3F',
    margin: '0 0 16px 0'
  },
  instructionsList: {
    margin: 0,
    paddingLeft: '20px',
    color: '#4A5568',
    fontSize: '14px',
    lineHeight: '1.6'
  },
  subList: {
    margin: '4px 0 0 20px',
    paddingLeft: 0,
    color: '#6C757D'
  },
  uploadArea: {
    marginBottom: '24px'
  },
  fileZone: {
    marginBottom: '20px'
  },
  fileLabel: {
    display: 'block',
    cursor: 'pointer'
  },
  fileInput: {
    display: 'none'
  },
  fileButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 24px',
    backgroundColor: '#FFFFFF',
    border: '2px solid #E2E8F0',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '500',
    color: '#2D3748',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: '#2A7DE1',
      backgroundColor: '#F8FAFF'
    }
  },
  fileIcon: {
    fontSize: '20px'
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#F1F5F9',
    borderRadius: '12px',
    marginTop: '12px',
    fontSize: '14px',
    color: '#1E293B'
  },
  fileInfoIcon: {
    fontSize: '14px'
  },
  fileSize: {
    color: '#64748B',
    fontSize: '12px',
    marginLeft: 'auto'
  },
  uploadButton: {
    width: '100%',
    padding: '14px 24px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  buttonActive: {
    backgroundColor: '#2A7DE1',
    color: '#FFFFFF',
    ':hover': {
      backgroundColor: '#1E6BCF'
    }
  },
  buttonDisabled: {
    backgroundColor: '#E2E8F0',
    color: '#94A3B8',
    cursor: 'not-allowed'
  },
  spinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid #FFFFFF',
    borderTop: '2px solid transparent',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  progressContainer: {
    marginBottom: '20px',
    padding: '16px 20px',
    backgroundColor: '#F8F9FC',
    borderRadius: '12px',
    border: '1px solid #E8ECF0'
  },
  progressWrapper: {
    width: '100%'
  },
  progressTrack: {
    width: '100%',
    height: '8px',
    backgroundColor: '#E8ECF0',
    borderRadius: '4px',
    overflow: 'hidden',
    position: 'relative'
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#2A7DE1',
    borderRadius: '4px',
    transition: 'width 0.5s ease-in-out',
    background: 'linear-gradient(90deg, #2A7DE1, #6C63FF)',
    position: 'relative'
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
    fontSize: '13px',
    color: '#4A5568'
  },
  progressText: {
    fontWeight: '500',
    flex: 1
  },
  progressPercent: {
    fontWeight: '600',
    color: '#2A7DE1',
    marginLeft: '12px'
  },
  message: {
    padding: '14px 20px',
    borderRadius: '12px',
    fontSize: '14px',
    marginBottom: '24px',
    textAlign: 'center'
  },
  documentsList: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: '#F8F9FC',
    borderRadius: '12px',
    border: '1px solid #E8ECF0'
  },
  documentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '8px'
  },
  documentsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#0B1C3F',
    margin: 0
  },
  docItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    marginBottom: '8px',
    border: '1px solid #E8ECF0',
    transition: 'all 0.2s ease'
  },
  docItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1
  },
  docItemRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  docStatusIcon: {
    fontSize: '16px',
    width: '24px',
    textAlign: 'center'
  },
  docType: {
    fontWeight: '600',
    fontSize: '13px',
    color: '#1E293B',
    minWidth: '70px'
  },
  docStatusText: {
    fontSize: '13px',
    fontWeight: '500'
  },
  docLink: {
    color: '#FFFFFF',
    backgroundColor: '#2A7DE1',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '6px 14px',
    borderRadius: '6px',
    border: 'none',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#1E6BCF'
    }
  },
  docSize: {
    fontSize: '12px',
    color: '#6B7280'
  },
  docError: {
    fontSize: '13px',
    color: '#EF4444',
    fontWeight: '500'
  },
  docDownloading: {
    fontSize: '13px',
    color: '#8B5CF6',
    fontWeight: '500'
  },
  docWaiting: {
    fontSize: '13px',
    color: '#9CA3AF',
    fontWeight: '500'
  },
  processingDots: {
    display: 'inline-block',
    width: '4px',
    height: '4px',
    backgroundColor: '#8B5CF6',
    borderRadius: '50%',
    animation: 'pulse 0.8s ease-in-out infinite',
    marginLeft: '4px'
  },
  features: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'space-between',
    paddingTop: '24px',
    borderTop: '1px solid #E8ECF0',
    marginTop: '8px'
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1
  },
  featureIcon: {
    fontSize: '28px'
  },
  featureTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1E293B',
    margin: '0 0 4px 0'
  },
  featureDesc: {
    fontSize: '12px',
    color: '#64748B',
    margin: 0
  },
  footer: {
    backgroundColor: '#0B1C3F',
    padding: '32px 20px',
    textAlign: 'center'
  },
  footerContent: {
    maxWidth: '900px',
    margin: '0 auto',
    color: '#94A3B8',
    fontSize: '14px'
  },
  footerLink: {
    color: '#B0BEC5',
    textDecoration: 'none',
    marginTop: '8px',
    cursor: 'pointer',
    ':hover': {
      color: '#FFFFFF'
    }
  },
  footerEmail: {
    color: '#B0BEC5',
    textDecoration: 'none',
    ':hover': {
      color: '#FFFFFF',
      textDecoration: 'underline'
    }
  }
};

// Добавляем анимации
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.3; transform: scale(0.8); }
  }
`;
document.head.appendChild(styleSheet);

export default Home;